import { Recipe } from '@/types';
import { BrewfatherRecipe, BrewfatherService } from './BrewfatherService';

const mapBrewfatherRecipeType = (type: string): Recipe['type'] => {
	const typeMap: Record<string, Recipe['type']> = {
		'All Grain': 'all-grain',
		'Extract': 'extract',
		'Partial Mash': 'partial-mash',
		'BIAB': 'biab'
	};
	return typeMap[type] || 'all-grain';
};

const mapBrewfatherRecipeToRecipe = (bfRecipe: BrewfatherRecipe, userId: string): Recipe => {
	// Map ingredients
	const ingredients: any = {};
	if (bfRecipe.fermentables && bfRecipe.fermentables.length > 0) {
		ingredients.fermentables = bfRecipe.fermentables.map((f) => ({
			name: f.name,
			amount: f.amount,
			unit: (f.unit === 'kg' || f.unit === 'g' || f.unit === 'lb' || f.unit === 'oz') 
				? f.unit as 'kg' | 'g' | 'lb' | 'oz'
				: 'g'
		}));
	}
	if (bfRecipe.hops && bfRecipe.hops.length > 0) {
		ingredients.hops = bfRecipe.hops.map((h) => ({
			name: h.name,
			amount: h.amount,
			unit: (h.unit === 'g' || h.unit === 'oz') ? h.unit as 'g' | 'oz' : 'g',
			time: h.time,
			use: (h.use === 'boil' || h.use === 'dry hop' || h.use === 'first wort' || h.use === 'mash' || h.use === 'aroma')
				? h.use as 'boil' | 'dry hop' | 'first wort' | 'mash' | 'aroma'
				: 'boil'
		}));
	}
	if (bfRecipe.yeast && bfRecipe.yeast.length > 0) {
		ingredients.yeast = bfRecipe.yeast.map((y) => ({
			name: y.name,
			amount: y.amount,
			unit: (y.unit === 'pack' || y.unit === 'vial' || y.unit === 'g') 
				? y.unit as 'pack' | 'vial' | 'g' 
				: undefined
		}));
	}
	if (bfRecipe.other && bfRecipe.other.length > 0) {
		ingredients.other = bfRecipe.other.map((o) => ({
			name: o.name,
			amount: o.amount,
			unit: o.unit,
			use: o.use
		}));
	}

	// Map mash steps
	const mashSteps = bfRecipe.mash && bfRecipe.mash.length > 0
		? bfRecipe.mash.map((m) => ({
			name: m.name,
			temperature: m.temp,
			duration: m.time
		}))
		: undefined;

	// Extract style name - can be string, object with name property, or null
	let styleName: string | undefined = undefined;
	if (bfRecipe.style) {
		if (typeof bfRecipe.style === 'string') {
			styleName = bfRecipe.style;
		} else if (typeof bfRecipe.style === 'object' && bfRecipe.style !== null && 'name' in bfRecipe.style) {
			styleName = (bfRecipe.style as { name: string }).name;
		}
	}

	return {
		id: bfRecipe._id,
		name: bfRecipe.name,
		style: styleName,
		type: mapBrewfatherRecipeType(bfRecipe.type),
		originalGravity: bfRecipe.og,
		finalGravity: bfRecipe.fg,
		abv: bfRecipe.abv,
		ibu: bfRecipe.ibu,
		srm: bfRecipe.color,
		description: bfRecipe.description,
		ingredients: Object.keys(ingredients).length > 0 ? ingredients : undefined,
		mashSteps,
		brewfatherRecipeId: bfRecipe._id,
		userId,
		createdAt: new Date().toISOString(),
		updatedAt: new Date().toISOString()
	};
};

export const RecipeService = {
	listRecipes: async (userId: string, authHeader: Record<string, string>): Promise<{ items: Recipe[] }> => {
		const brewfatherRecipes = await BrewfatherService.getRecipes(userId, authHeader);
		const recipes = brewfatherRecipes.map(bf => mapBrewfatherRecipeToRecipe(bf, userId));
		return { items: recipes };
	},

	getRecipe: async (recipeId: string, userId: string, authHeader: Record<string, string>): Promise<{ item: Recipe }> => {
		const bfRecipe = await BrewfatherService.getRecipe(recipeId, userId, authHeader);
		return { item: mapBrewfatherRecipeToRecipe(bfRecipe, userId) };
	}
};
